// Pure `before_send` steps. Each takes a PostHog CaptureResult and returns
// it changed, or null to drop it. No SDK import here, so these run in plain
// Node tests.
//
// Order is fixed by `buildBeforeSend`:
//   1. describeUrlStateStep - the URL is the router state, so it is kept
//                             verbatim: every query param is flattened into
//                             a `url_<param>` property, the derived
//                             structure (`screen`, `scope_dims`, ...) is
//                             added, and `$pathname` becomes a virtual route
//                             from the same vocabulary so PostHog's page
//                             tooling has pages to group by; a pageview
//                             for the page already sent becomes
//                             `ui_control_used`, or is dropped; only
//                             credential-bearing params are scrubbed from
//                             every URL-shaped property and heatmap key
//   2. redactExceptions      - strip response bodies out of error messages
//   3. enforceRegistry       - drop anything the registry does not declare
import { experimentalEnabled, securityTabEnabled } from "../featureFlags.js";
import { readExplorerStrip, requestedSetting, requestedViewFor, resolveMode, scopeSegments, shownModes, shownViewModes } from "../routeModel.js";
import {
    ADMIN_VIEW_MODES,
    CANVAS_IDS,
    CUSTOM_CANVAS,
    DASHBOARD_SUBVIEW,
    DIMENSION_IDS,
    EVENT_NAME,
    MODE,
    ROUTER_PARAM,
    TAB_KIND,
    UI_CONTROL,
    UI_CONTROL_BY_PARAM,
    UNKNOWN_DIMENSION,
} from "./surfaceVocabulary.js";

// Query params that carry a credential or a one-time secret: the WorkOS
// authorization code and state on the sign-in landing, password reset
// tokens, IdP session ids. They are replaced, never sent, whatever the
// fidelity decision for the rest of the URL is.
export const CREDENTIAL_PARAMS = Object.freeze([
    "code",
    "state",
    "password_reset_token",
    "session_id",
    "token",
    "access_token",
    "refresh_token",
    "id_token",
    "client_secret",
]);
const FLEET_PRIVATE_PARAMS = ["fleetSearch", "fleetSelected", "fleetGroup"];
const REDACTED = "redacted";
// Base for parsing relative URLs; never sent.
const PLACEHOLDER_HOST = "placeholder.invalid";

const URL_KEY = /(url|referrer)$/i;
// Only strings that look like a URL or a path are rewritten. The SDK also
// sends sentinels such as "$direct" for a missing referrer; those pass.
const URL_SHAPED = /^(?:[a-z][a-z0-9+.-]*:\/\/|\/)/i;
function parseUrl(value) {
    if (typeof value !== "string" || value === "") return null;
    try {
        return new URL(value, `http://${PLACEHOLDER_HOST}`);
    } catch {
        return null;
    }
}

// The URL with credential params replaced and everything else untouched.
export function scrubCredentialParams(value) {
    if (typeof value !== "string" || !URL_SHAPED.test(value)) return value;
    const url = parseUrl(value);
    if (!url) return value;
    let changed = false;
    for (const key of [...CREDENTIAL_PARAMS, ...FLEET_PRIVATE_PARAMS]) {
        if (url.searchParams.has(key)) {
            url.searchParams.set(key, REDACTED);
            changed = true;
        }
    }
    if (!changed) return value;
    const origin = url.hostname === PLACEHOLDER_HOST ? "" : url.origin;
    return `${origin}${url.pathname}${url.search}${url.hash}`;
}

// Every query param as a `url_<name>` property, credential params redacted,
// repeated params joined in order with `|`. Param names are normalized so
// they are valid property keys.
export function flattenUrlParams(value) {
    const url = parseUrl(value);
    if (!url) return {};
    const out = {};
    for (const [rawKey, rawValue] of url.searchParams) {
        const key = `url_${rawKey.replace(/[^a-zA-Z0-9_]/g, "_")}`;
        const v = [...CREDENTIAL_PARAMS, ...FLEET_PRIVATE_PARAMS].includes(rawKey) ? REDACTED : rawValue;
        out[key] = key in out ? `${out[key]}|${v}` : v;
    }
    return out;
}

// The dimensions of an encoded scope path; one outside the vocabulary is
// counted as unknown rather than echoed.
function scopeDims(path) {
    return scopeSegments(path).map(({ dim }) => (DIMENSION_IDS.includes(dim) ? dim : UNKNOWN_DIMENSION));
}

// The structural description of where the person is, read from the URL the
// way the app's router reads it (lib/routeModel.js), short of the fallback
// the router applies when the caller cannot use the requested mode. Sits
// beside the verbatim `url_*` params so insights can group by `screen` or
// `scope_dims` without parsing URLs.
export function describeUrlState(value) {
    const url = parseUrl(value);
    if (!url) return {};
    const p = url.searchParams;
    const securityEnabled = securityTabEnabled();
    const viewModes = shownViewModes({ securityEnabled });
    const mode = resolveMode(p, {
        validModes: shownModes({ securityEnabled, experimentalEnabled: experimentalEnabled() }),
        defaultMode: MODE.TRIAGE,
        viewModes,
    });
    const adminView = requestedViewFor(p, mode, viewModes);
    // Settings names what is open by its address (`?setting=`); an old link
    // still names a page until Settings rewrites it.
    // Governance is one page: an old agent policy link's view names nothing in it.
    const view = mode === MODE.SETTINGS ? (requestedSetting(p) ?? adminView) : mode === MODE.GOVERNANCE ? null : adminView;
    // The app lives on `/`; any other pathname is a code-defined route such
    // as the OAuth or MCP callback pages, and is the screen by itself.
    const routePath = url.pathname && url.pathname !== "/" ? url.pathname.replace(/^\/+|\/+$/g, "") : null;
    const range = p.get(ROUTER_PARAM.RANGE) || null;
    const lens = p.get(ROUTER_PARAM.LENS) || null;
    const rawFocus = p.get(ROUTER_PARAM.FOCUS);
    const focus = DIMENSION_IDS.includes(rawFocus) ? rawFocus : null;
    // The Explore strip is described only where the URL carries it.
    const explorer = p.has(ROUTER_PARAM.TABS) || p.has(ROUTER_PARAM.SCOPE) ? readExplorerStrip(p) : { tabs: [], activeIdx: 0 };
    const active = explorer.tabs[explorer.activeIdx];
    const dims = active?.kind === TAB_KIND.SCOPE ? scopeDims(active.payload) : [];
    const steers = p.get(ROUTER_PARAM.STEERS);
    // `cv` outlives the canvas: it stays in the URL when the person moves
    // on to Explore, so it names a canvas only where one is on screen, the
    // dashboard home or Explore's canvases lens.
    const rawCanvas = p.get(ROUTER_PARAM.CANVAS);
    const showsCanvas = mode === MODE.DASHBOARD && p.get(ROUTER_PARAM.MODE) !== "explore";
    const canvas = rawCanvas && showsCanvas ? (CANVAS_IDS.includes(rawCanvas) ? rawCanvas : CUSTOM_CANVAS) : null;
    const out = {
        screen: routePath ?? (view ? `${mode}/${view}` : mode),
        mode,
        view,
        range,
        lens,
        focus,
        scope_dims: dims,
        scope_depth: dims.length,
        tab_kinds: explorer.tabs.map((t) => t.kind),
        tab_count: explorer.tabs.length,
        has_query: Boolean(p.get(ROUTER_PARAM.FLEET_SEARCH)) || Boolean(p.get(ROUTER_PARAM.QUERY)) || Boolean(p.get(ROUTER_PARAM.TRACE_QUERY)) || (Boolean(steers) && steers !== "{}"),
        detail_open: p.has(ROUTER_PARAM.FLEET_SELECTED) || p.has(ROUTER_PARAM.LOG),
        canvas,
    };
    for (const key of Object.keys(out)) {
        if (out[key] === null) delete out[key];
    }
    return out;
}

function scrubUrlKeys(bag) {
    if (!bag || typeof bag !== "object") return;
    for (const key of Object.keys(bag)) {
        if (URL_KEY.test(key) && typeof bag[key] === "string") {
            bag[key] = scrubCredentialParams(bag[key]);
        }
    }
}

// The route PostHog's page tooling groups by: Web Analytics pages, entry and
// exit paths, the Paths insight. The app itself lives on `/` and routes by
// query params, so `$pathname` is rewritten to this. Built only from the
// static vocabularies, so it never carries an id, a slug or a label; those
// stay verbatim on `$current_url` and the `url_*` params.
export function virtualPathname(value) {
    const url = parseUrl(value);
    if (!url) return null;
    if (url.pathname && url.pathname !== "/") return `/${url.pathname.replace(/^\/+|\/+$/g, "")}`;
    const p = url.searchParams;
    const state = describeUrlState(value);
    const segments = [state.mode];
    switch (state.mode) {
        case MODE.DASHBOARD:
            if (state.canvas) segments.push(DASHBOARD_SUBVIEW.CANVAS);
            else if (p.has(ROUTER_PARAM.CHAT)) segments.push(DASHBOARD_SUBVIEW.CHAT);
            break;
        default:
            if (state.view) segments.push(state.view);
    }
    return `/${segments.join("/")}`;
}

// The derived fields that make a pageview a new page. The router also
// mirrors search text, expanded rows and sort order into the URL a quarter
// second after they settle, and the Explore search box writes its text on
// every keystroke; a URL change that leaves these fields alone is not a page
// change, and its pageview becomes `ui_control_used` or is dropped. Choosing
// another canvas is a new page, so canvas views count.
const PAGE_FIELDS = ["$pathname", "screen", "scope_dims", "tab_kinds", "lens", "view", "detail_open", "canvas"];

function pageKey(props) {
    return JSON.stringify(PAGE_FIELDS.map((field) => props[field] ?? null));
}

// Heatmap batches are keyed by the page's full URL, which bypasses the
// URL-shaped property names `scrubUrlKeys` looks for.
function scrubHeatmapUrls(props) {
    const data = props.$heatmap_data;
    if (!data || typeof data !== "object") return;
    const scrubbed = {};
    for (const [url, points] of Object.entries(data)) {
        const key = scrubCredentialParams(url);
        scrubbed[key] = scrubbed[key] && Array.isArray(points) ? scrubbed[key].concat(points) : points;
    }
    props.$heatmap_data = scrubbed;
}

// Each query param's value, repeated params joined, credential params left
// out: what a later URL is compared against.
function paramValues(value) {
    const url = parseUrl(value);
    const out = new Map();
    if (!url) return out;
    for (const [key, v] of url.searchParams) {
        if (CREDENTIAL_PARAMS.includes(key)) continue;
        out.set(key, out.has(key) ? `${out.get(key)}|${v}` : v);
    }
    return out;
}

// The controls whose params differ between two URLs of the same page,
// sorted, each named once.
export function changedControls(before, after) {
    const controls = new Set();
    for (const key of new Set([...before.keys(), ...after.keys()])) {
        if (before.get(key) !== after.get(key)) controls.add(UI_CONTROL_BY_PARAM[key] ?? UI_CONTROL.OTHER);
    }
    return [...controls].sort();
}

// A search box writes on every keystroke; repeats of one control set on one
// page within this window are one use.
const CONTROL_REPEAT_MS = 2000;
// A page writes its own state into the URL as it loads: redirects, the
// table state mirror, Discovery's defaults. Measured on devint and staging,
// those writes land within 0.8 seconds of the page and the first real use
// after 1.3, so a same-page change this soon after a new page is the page's.
const PAGE_SETTLE_MS = 1000;

// One instance per SDK init: it remembers the last pageview's virtual route
// so `$pageleave` and later pageviews report the route being left rather
// than the SDK's literal `/`, and the last page sent. A pageview for the
// same page is not a page change: it becomes `ui_control_used`, naming the
// controls whose params changed, or is dropped when nothing did.
export function createUrlStateStep({ now = () => Date.now() } = {}) {
    let lastPath = null;
    let lastPage = null;
    let lastParams = null;
    let lastControl = null;
    let lastPageAt = -Infinity;
    return function describeUrlStateStep(cr) {
        if (!cr) return cr;
        const props = cr.properties ?? (cr.properties = {});
        const current = props.$current_url;
        if (typeof current === "string") {
            Object.assign(props, describeUrlState(current), flattenUrlParams(current));
            const vpath = virtualPathname(current);
            if (vpath) {
                props.$pathname = vpath;
                if (cr.event === EVENT_NAME.PAGEVIEW) {
                    const page = pageKey(props);
                    const params = paramValues(current);
                    const before = lastParams;
                    lastParams = params;
                    const at = now();
                    if (page === lastPage) {
                        // The settled URL is still the baseline for later changes.
                        if (at - lastPageAt < PAGE_SETTLE_MS) return null;
                        const controls = before ? changedControls(before, params) : [];
                        if (controls.length === 0) return null;
                        const key = `${page}#${controls.join(",")}`;
                        const repeat = lastControl && lastControl.key === key && at - lastControl.at < CONTROL_REPEAT_MS;
                        lastControl = { key, at };
                        if (repeat) return null;
                        cr.event = EVENT_NAME.UI_CONTROL_USED;
                        props.controls = controls;
                        // The SDK counted this as a page; its page timing belongs to no page here.
                        for (const k of Object.keys(props)) {
                            if (k.startsWith("$prev_pageview_")) delete props[k];
                        }
                        delete props.navigation_type;
                    } else {
                        lastPage = page;
                        lastPageAt = at;
                        lastControl = null;
                        if (lastPath !== null && "$prev_pageview_pathname" in props) props.$prev_pageview_pathname = lastPath;
                        lastPath = vpath;
                    }
                } else if (cr.event === EVENT_NAME.PAGELEAVE) {
                    props.$prev_pageview_pathname = lastPath ?? vpath;
                }
            }
            if (typeof props.$session_entry_url === "string") {
                const entry = virtualPathname(props.$session_entry_url);
                if (entry) props.$session_entry_pathname = entry;
            }
        }
        scrubUrlKeys(props);
        scrubUrlKeys(props.$set);
        scrubUrlKeys(props.$set_once);
        scrubUrlKeys(cr.$set);
        scrubUrlKeys(cr.$set_once);
        scrubHeatmapUrls(props);
        return cr;
    };
}

export const describeUrlStateStep = createUrlStateStep();

// `restClient.expectOk` formats errors as "METHOD path status: <body>" and
// the body can carry tenant content. Keep the shape, drop the body.
const REST_ERROR = /^((?:GET|POST|PUT|PATCH|DELETE) \S+ \d{3}):[\s\S]*$/;
const MAX_MESSAGE = 300;

export function redactMessage(message) {
    if (typeof message !== "string") return message;
    const m = REST_ERROR.exec(message);
    const kept = m ? m[1] : message;
    return kept.length > MAX_MESSAGE ? `${kept.slice(0, MAX_MESSAGE)}...` : kept;
}

export function redactExceptionMessages(cr) {
    if (!cr || cr.event !== EVENT_NAME.EXCEPTION) return cr;
    const props = cr.properties;
    if (!props) return cr;
    if (Array.isArray(props.$exception_list)) {
        for (const item of props.$exception_list) {
            if (item && typeof item.value === "string") item.value = redactMessage(item.value);
        }
    }
    if (typeof props.$exception_message === "string") {
        props.$exception_message = redactMessage(props.$exception_message);
    }
    return cr;
}

const dropEverything = () => null;

// `enforceRegistry` is events.js's enforcer, which client.js passes in once
// it has loaded the registry; without one, nothing is sent.
export function buildBeforeSend({ enforceRegistry = dropEverything } = {}) {
    return [
        createUrlStateStep(),
        redactExceptionMessages,
        enforceRegistry,
    ];
}
